import { useState } from 'react';

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d6q3pik4k19g20dvr3vg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      setNewsletterStatus('error');
    }
  };

  return (
    <footer className="bg-white text-gray-800 pt-20 pb-8 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <img
              src="https://storage.readdy-site.link/project_files/1fe59d6f-f04b-46cd-9337-87602fe44c2d/87e17134-81de-4225-99fd-31fa6737ddb3_BT-logo.png?v=9f968b49a8251d6db01111c3dfc346d0"
              alt="British Custom Tailors Logo"
              style={{
                height: '100px',
                width: 'auto',
                display: 'block',
                marginBottom: '16px',
              }}
            />
            <p className="text-gray-500 mb-6 leading-relaxed">
              Exquisite hand-made custom clothing for men and women. Premium quality and perfect fit guaranteed.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/custom.clothiers.wellesley/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-[#C9A961] hover:text-white transition-colors">
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a href="https://www.yelp.com/biz/custom-clothiers-wellesley-hills" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-[#C9A961] hover:text-white transition-colors">
                <i className="ri-yelp-fill text-lg"></i>
              </a>
              <a href="https://www.linkedin.com/in/harry-and-michael-narwani-87b0b916/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-[#C9A961] hover:text-white transition-colors">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Services</h4>
            <ul className="space-y-3">
              <li><a href="/services#custom-suits" className="text-gray-500 hover:text-[#C9A961] transition-colors whitespace-nowrap">Custom Suits</a></li>
              <li><a href="/services#custom-shirts" className="text-gray-500 hover:text-[#C9A961] transition-colors whitespace-nowrap">Custom Shirts</a></li>
              <li><a href="/services#alterations" className="text-gray-500 hover:text-[#C9A961] transition-colors whitespace-nowrap">Alterations</a></li>
              <li><a href="/services#house-calls" className="text-gray-500 hover:text-[#C9A961] transition-colors whitespace-nowrap">House Calls</a></li>
              <li><a href="/services#style-consultation" className="text-gray-500 hover:text-[#C9A961] transition-colors whitespace-nowrap">Style Consultation</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <i className="ri-phone-line text-[#C9A961] mt-1"></i>
                <div>
                  <div className="text-sm text-gray-400">Phone</div>
                  <a href="tel:866-857-8487" className="text-gray-800 hover:text-[#C9A961] transition-colors font-semibold whitespace-nowrap">866-857-8487</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-mail-line text-[#C9A961] mt-1"></i>
                <div>
                  <div className="text-sm text-gray-400">Email</div>
                  <a href="mailto:info@britishtailors.com" className="text-gray-800 hover:text-[#C9A961] transition-colors whitespace-nowrap">info@britishtailors.com</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-[#C9A961] mt-1"></i>
                <div>
                  <div className="text-sm text-gray-400">Service Area</div>
                  <div className="text-gray-800">Nationwide Coverage</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Join Our Mailing List</h4>
            <p className="text-gray-500 mb-4 text-sm">
              Get exclusive offers and style tips delivered to your inbox.
            </p>
            <form data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <input
                type="email"
                name="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Your email"
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#C9A961] focus:border-transparent transition-all text-sm"
              />
              <button
                type="submit"
                className="w-full bg-[#C9A961] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
            {newsletterStatus === 'success' && (
              <p className="text-green-600 text-sm mt-2">Thank you for subscribing!</p>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-red-500 text-sm mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2025 British Custom Tailors. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-[#C9A961] transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-[#C9A961] transition-colors whitespace-nowrap">Terms of Service</a>
              <a href="/services" className="text-gray-400 hover:text-[#C9A961] transition-colors whitespace-nowrap">Services</a>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#C9A961] p-4 shadow-2xl lg:hidden z-40">
        <div className="flex gap-3">
          <a 
            href="tel:866-857-8487" 
            className="flex-1 bg-white text-[#C9A961] px-6 py-3 rounded-full font-bold text-center hover:bg-gray-100 transition-all whitespace-nowrap"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Now
          </a>
          <a 
            href="#consultation" 
            className="flex-1 bg-gray-900 text-white px-6 py-3 rounded-full font-bold text-center hover:bg-gray-800 transition-all whitespace-nowrap"
          >
            Book Now
          </a>
        </div>
      </div>
    </footer>
  );
}